import { memo } from 'react';
import { styled } from '@mui/material/styles';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { RemirrorJSON } from '@remirror/core';
import { type Chat } from '@trinity/chat';
import { Citation } from '@trinity/chat-components/components/Markdown/Citations';
import { useHasChatAtMentionFilterEnabled } from '../../containers/Chat/selectors';
import { useOpenNewChatForSubjectAction } from '../Chat/hooks/useOpenNewChatForSubjectAction';
import { useSetChatInputText } from '../Chat/hooks/useSetChatInputText';
import { ChatMarkdown } from '../Markdown/Markdown';
import { TextQueryHint } from '../presentation/Message.QueryHint';
import { TableActionsContainer } from './Table.RowContainer';

const ARROW_BUTTON_DIRECTIVE = 'arrowButton';
const INFO_CHIP_DIRECTIVE = 'infoChip';
const CITATION_MARKER_PATTERN = /\^\d+[a-z]*\^/;
const CITATION_VALUE_PATTERN = /^\^([^^]+)\^$/;

type AttachmentCellValueObject = {
  value: string;
  metadata?: Chat.AttachmentTableCellMetadata;
};

type AttachmentTableColDef = {
  attachmentCellMetadata?: Chat.AttachmentTableCellMetadata;
};

type CitationCellProps = {
  value: string;
};

const CitationCell = memo((props: CitationCellProps) => {
  return <Citation style={{ maxHeight: 18, minWidth: 12, minHeight: 18 }} value={props.value} />;
});

type ActionMarkdownCellProps = {
  row: Chat.AttachmentTableRow;
  value: string;
};

const ActionMarkdownCell = memo(
  (props: ActionMarkdownCellProps) => {
    return (
      <TableActionsContainer row={props.row}>
        <StyledCell>
          <ChatMarkdown value={props.value} />
        </StyledCell>
      </TableActionsContainer>
    );
  },
  (previousProps, nextProps) => {
    return previousProps.value === nextProps.value && previousProps.row === nextProps.row;
  }
);

const getCellValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  return String(value ?? '');
};

const isAttachmentCellValueObject = (value: unknown): value is AttachmentCellValueObject => {
  return typeof value === 'object' && value !== null && 'value' in value;
};

const getRowStringValue = (row: Chat.AttachmentTableRow, field: string | undefined): string | undefined => {
  if (!field) {
    return undefined;
  }

  const rawValue = row[field];
  const value = isAttachmentCellValueObject(rawValue) ? rawValue.value : rawValue;

  return typeof value === 'string' ? value : undefined;
};

const getRowSubjectId = (
  row: Chat.AttachmentTableRow,
  field: string | undefined
): number | string | undefined => {
  if (!field) {
    return undefined;
  }

  const rawValue = row[field];
  const value = isAttachmentCellValueObject(rawValue) ? rawValue.value : rawValue;

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  return undefined;
};

const getCellMetadata = (
  params: GridRenderCellParams<Chat.AttachmentTableRow, unknown>
): Chat.AttachmentTableCellMetadata | undefined => {
  const rawCellValue = params.row[params.field];
  const cellMetadata = isAttachmentCellValueObject(rawCellValue) ? rawCellValue.metadata : undefined;
  const columnMetadata = (params.colDef as AttachmentTableColDef).attachmentCellMetadata;

  return cellMetadata ?? columnMetadata;
};

const getCitationValue = (cellValue: string): string | null => {
  if (!CITATION_MARKER_PATTERN.test(cellValue)) {
    return null;
  }

  return cellValue.replace(CITATION_VALUE_PATTERN, '$1');
};

const hasTableActionDirective = (cellValue: string): boolean => {
  return cellValue.includes(ARROW_BUTTON_DIRECTIVE) || cellValue.includes(INFO_CHIP_DIRECTIVE);
};

const buildProfileQuery = (subjectName: string): string => {
  return `Show me the profile for ${subjectName}`;
};

function buildProfileQueryDoc(data: {
  subjectId: number | string;
  subjectName: string;
  subjectKind?: string;
  subjectLink?: string;
}): RemirrorJSON {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text: 'Show me the profile for ',
          },
          {
            type: 'mentionAtom',
            attrs: {
              id: String(data.subjectId),
              label: data.subjectName,
              name: 'subject',
              entityType: data.subjectKind ?? '',
              ...(data.subjectLink !== undefined ? { link: data.subjectLink } : {}),
            },
          },
        ],
      },
    ],
  };
}

type OpenNewChatForSubjectCellProps = {
  params: GridRenderCellParams<Chat.AttachmentTableRow, unknown>;
  action: Chat.AttachmentTableOpenNewChatForSubjectAction;
};

const OpenNewChatForSubjectCell = ({ params, action }: OpenNewChatForSubjectCellProps) => {
  const openNewChatForSubject = useOpenNewChatForSubjectAction();
  const setChatInputText = useSetChatInputText();
  const hasChatAtMentionFilterEnabled = useHasChatAtMentionFilterEnabled();
  const subjectId = getRowSubjectId(params.row, action.idField ?? 'id');
  const subjectName = getRowStringValue(params.row, action.nameField ?? 'name');

  if (!subjectName || subjectId === undefined) {
    return null;
  }

  const subjectKind = getRowStringValue(params.row, action.kindField) ?? action.kind;
  const profileQuery = buildProfileQuery(subjectName);
  const profileQueryDoc = buildProfileQueryDoc({
    subjectId,
    subjectName,
    subjectKind,
    subjectLink: action.link,
  });

  return (
    <StyledViewProfileButton
      value={action.label ?? 'View'}
      onClick={() => {
        if (action.openInNewChat === false || !action.chatContextIdentifier) {
          setChatInputText(
            profileQuery,
            hasChatAtMentionFilterEnabled ? { focus: true, richDoc: profileQueryDoc } : { focus: true }
          );
          return;
        }

        const didDispatch = openNewChatForSubject({
          subject: {
            id: subjectId,
            name: subjectName,
            ...(subjectKind !== undefined ? { kind: subjectKind } : {}),
            ...(action.link !== undefined ? { link: action.link } : {}),
          },
          chatContextIdentifier: action.chatContextIdentifier,
          pendingQuery: profileQuery,
        });

        if (!didDispatch) {
          setChatInputText(
            profileQuery,
            hasChatAtMentionFilterEnabled ? { focus: true, richDoc: profileQueryDoc } : { focus: true }
          );
        }
      }}
    />
  );
};

export const TableCell = (params: GridRenderCellParams<Chat.AttachmentTableRow, unknown>) => {
  const cellMetadata = getCellMetadata(params);
  const cellAction = cellMetadata?.action;
  const cellValue = getCellValue(params.value);
  const citationValue = getCitationValue(cellValue);

  if (cellAction?.type === 'open-new-chat-for-subject') {
    return <OpenNewChatForSubjectCell params={params} action={cellAction} />;
  }

  if (citationValue !== null) {
    return <CitationCell value={citationValue} />;
  }

  if (hasTableActionDirective(cellValue)) {
    return <ActionMarkdownCell row={params.row} value={cellValue} />;
  }

  return cellValue;
};

const StyledCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
});

const StyledViewProfileButton = styled(TextQueryHint)({
  minWidth: 115,
  maxWidth: 180,
});
